﻿package com.mgacreative.hotelru.domain.repository

import com.mgacreative.hotelru.core.tenant.TenantSession
import com.mgacreative.hotelru.domain.model.Department
import com.mgacreative.hotelru.domain.repository.DepartmentRepository
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.serialization.Serializable

class DepartmentRepositoryImpl(
    private val supabase: SupabaseClient,
    private val tenantSession: TenantSession
) : DepartmentRepository {

    @Serializable
    private data class InsertDepartmentDto(
        @kotlinx.serialization.SerialName("tenant_id")
        val tenantId: String,
        val name: String,
        val permissions: List<String>
    )

    @Serializable
    private data class UpdateDepartmentDto(
        val name: String,
        val permissions: List<String>
    )

    override fun getDepartments(): Flow<List<Department>> = flow {
        val tenantId = tenantSession.sessionState.value.tenantId
        if (tenantId != null) {
            val items = supabase.postgrest["departments"]
                .select {
                    filter {
                        eq("tenant_id", tenantId)
                    }
                }
                .decodeList<Department>()
            emit(items)
        } else {
            emit(emptyList())
        }
    }

    override suspend fun addDepartment(name: String, permissions: List<String>): Result<Unit> {
        return try {
            val tenantId = tenantSession.sessionState.value.tenantId
                ?: return Result.failure(Exception("Tenant ID not found"))

            val dto = InsertDepartmentDto(
                tenantId = tenantId,
                name = name,
                permissions = permissions
            )

            // select() forces Supabase to return the inserted row. 
            // If it fails (e.g., silently blocked by RLS), decodeSingle() will throw an error.
            supabase.postgrest["departments"].insert(dto) {
                select()
            }.decodeSingle<Department>()

            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateDepartment(id: String, name: String, permissions: List<String>): Result<Unit> {
        return try {
            val dto = UpdateDepartmentDto(
                name = name,
                permissions = permissions
            )

            supabase.postgrest["departments"].update(dto) {
                filter {
                    eq("id", id)
                }
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun deleteDepartment(id: String): Result<Unit> {
        return try {
            supabase.postgrest["departments"].delete {
                filter {
                    eq("id", id)
                }
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}

